/**
 * The Monitor's inbox work (PRD-0075 R-B5, R-B6): the bell, the reader's visit, the live stream,
 * triage, the standing sources and the watchlists' edits. The stream keeps the inbox and the bell
 * current without a reload; the bell also reads itself again every minute, in case a push is lost.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type AlertDto,
  caseMonitorApi,
  type MonitorUnreadDto,
  type StandingSourceDto,
  type TriageRequest,
} from '@/services/api/case-monitor'
import { ApiError, apiClient } from '@/services/api/client'
import { investigationsApi } from '@/services/api/investigations'
import { retryUnlessRefused } from '@/services/api/retry'
import { useInvestigationsStoreApiOptional } from '@/stores/investigations-store-provider'
import type { Investigation } from '@/types/investigation'
import { followMonitorStream, type MonitorStreamEvent } from './monitor-stream'
import { monitorKeys } from './useMonitor'

const TRIAGE = 'cases.monitor.triage'
/** The bell reads itself again this often, whatever the stream says. */
const BELL_POLL_MS = 60_000

export const monitorInboxKeys = {
  unread: (caseId: string) => [...monitorKeys.all(caseId), 'unread'] as const,
  sources: (caseId: string) => [...monitorKeys.all(caseId), 'sources'] as const,
  investigations: (caseId: string) =>
    [...monitorKeys.all(caseId), 'investigation-choices'] as const,
}

/** The reader's unread alerts in the case: the bell's count (0 while unknown). */
export function useMonitorUnreadCount(caseId: string | undefined): number {
  const unread = useQuery<MonitorUnreadDto>({
    queryKey: monitorInboxKeys.unread(caseId ?? ''),
    queryFn: ({ signal }) => caseMonitorApi.unread(caseId ?? '', signal),
    enabled: Boolean(caseId),
    refetchInterval: BELL_POLL_MS,
    retry: retryUnlessRefused,
  })
  return unread.data?.count ?? 0
}

/**
 * The reader is on the case's Monitor: their visit is marked when the page opens and again when a
 * newer alert (`newest`) arrives while it is open, and the bell drops to zero. Answers the visit
 * before this one, which tells the rows the reader has not seen yet (`undefined` until marked).
 */
export function useMonitorVisit(
  caseId: string,
  newest: string | undefined
): string | null | undefined {
  const queryClient = useQueryClient()
  const [since, setSince] = useState<string | null | undefined>(undefined)
  // biome-ignore lint/correctness/useExhaustiveDependencies: a newer alert while the page is open is a new visit
  useEffect(() => {
    let current = true
    caseMonitorApi.markSeen(caseId).then(
      mark => {
        if (!current) return
        setSince(previous => (previous === undefined ? mark.previous_seen_at : previous))
        queryClient.setQueryData<MonitorUnreadDto>(monitorInboxKeys.unread(caseId), {
          count: 0,
          seen_at: mark.seen_at,
        })
      },
      () => undefined // the bell keeps its count until the next visit is marked
    )
    return () => {
      current = false
    }
  }, [caseId, newest, queryClient])
  return since
}

/** Follows the case's Monitor stream while the case is open: the inbox and the bell read again on a push. */
export function useMonitorLiveUpdates(caseId: string | undefined): void {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!caseId) return
    const controller = new AbortController()
    const refresh = (event: MonitorStreamEvent) => {
      void queryClient.invalidateQueries({ queryKey: monitorKeys.alerts(caseId) })
      if (event.type !== 'alert_triaged') {
        void queryClient.invalidateQueries({ queryKey: monitorInboxKeys.unread(caseId) })
      }
    }
    void followMonitorStream({
      url: caseMonitorApi.streamUrl(caseId),
      signal: controller.signal,
      headers: () => apiClient.getAuthHeaders(),
      onEvent: refresh,
    })
    return () => controller.abort()
  }, [caseId, queryClient])
}

export interface TriageCommand {
  alertIds: readonly string[]
  request: TriageRequest
}

/** Triage an alert or a group; an accepted one refreshes the case's investigations, which now hold its pins. */
export function useAlertTriage(caseId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const investigations = useInvestigationsStoreApiOptional()
  return useMutation<AlertDto[], unknown, TriageCommand>({
    mutationFn: async ({ alertIds, request }) =>
      (await caseMonitorApi.triageAll(caseId, alertIds, request)).alerts,
    onSuccess: ([alert]) => {
      if (alert?.state !== 'accepted') return
      toast.success(
        t(`${TRIAGE}.acceptedToast`, { investigation: alert.investigation_title ?? '' })
      )
      void investigations?.getState().fetchAll()
      void queryClient.invalidateQueries({ queryKey: monitorInboxKeys.investigations(caseId) })
    },
    onError: error =>
      toast.error(
        t(
          error instanceof ApiError && error.status === 409
            ? `${TRIAGE}.locked`
            : `${TRIAGE}.failed`
        )
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: monitorKeys.alerts(caseId) }),
  })
}

/** The case's investigations an alert can go into, read while the accept dialog is open. */
export function useInvestigationChoices(caseId: string, enabled: boolean) {
  return useQuery<Investigation[]>({
    queryKey: monitorInboxKeys.investigations(caseId),
    queryFn: () => investigationsApi.list(caseId),
    enabled: enabled && Boolean(caseId),
    retry: retryUnlessRefused,
  })
}

export function useStandingSources(caseId: string) {
  return useQuery<StandingSourceDto[]>({
    queryKey: monitorInboxKeys.sources(caseId),
    queryFn: async ({ signal }) => (await caseMonitorApi.listSources(caseId, signal)).sources,
    enabled: Boolean(caseId),
    retry: retryUnlessRefused,
  })
}
